import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import CloseIcon from '~Assets/icons/close.svg';
import Icon from '~ComponentsImproved/Icon';
import Modal from '~ComponentsImproved/Modal';
import { useButton, useI18n } from '~Hooks';
import bLogo from '~Assets/Logos/b_logo_black.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { TransparentButton } from '~Styles';
import { DID_YOU_KNOWS } from './consts';
import { handyBreakpoint } from '~Styles/breakpoints';

const entryAnim = keyframes`
  0%, 50% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const Wrapper = styled.div<{
  yOffset?: number;
  animateOnMount?: boolean;
}>`
  display: flex;
  flex-direction: row;
  top: ${(props) => props.yOffset || 0}px;
  left: 0;
  bottom: 0;
  right: 0;
  overflow: hidden;
  box-sizing: border-box;
  position: fixed;
  z-index: 100;
  background: var(--colorBlueGray1100);
  animation: ${entryAnim} 500ms ease-out;
  animation-fill-mode: forwards;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  position: relative;
  padding: 20px;
  box-sizing: border-box;

  ${handyBreakpoint} {
    padding: 12px;
  }
`;

const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  margin-bottom: 96px;

  ${handyBreakpoint} {
    margin-bottom: 88px;
  }
`;

const LogoContainer = styled.div`
  width: 100px;
  height: 100px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  margin-bottom: 40px;

  ${handyBreakpoint} {
    width: 72px;
    height: 72px;
    margin-bottom: 24px;
  }
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: invert(1);
`;

const DidYouKnowSection = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 60px;
  width: 320px;

  ${handyBreakpoint} {
    margin-bottom: 40px;
  }
`;

const FactTextWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  transition: opacity 0.5s ease-in-out;
`;

const ProgressBar = styled.div<{ progress: number }>`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  width: 240px;
  height: 22px;
  border-radius: 20px;
  color: var(--colorWhite);
  border: 2px solid;
  margin-top: 40px;

  &::before {
    content: '';
    position: absolute;
    margin: 2px;
    inset: 0 ${({ progress }) => 100 - progress}% 0 0;
    border-radius: inherit;
    background: currentColor;
    transition: inset 0.5s ease-out;
  }
`;

const ProgressText = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: var(--colorWhite);
  font-size: 14px;
  opacity: 0.7;

  ${handyBreakpoint} {
    font-size: 12px;
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 20px;

  ${handyBreakpoint} {
    margin-bottom: 12px;
  }
`;

const CloseButton = styled(TransparentButton)`
  color: var(--colorBlueGray200);
`;

interface Props {
  yOffset?: number;
  animateOnMount?: boolean;
  progress: number;
  progressText: string;
  onClose: () => void;
}

/** Loading screen for the Ordering screen in Tablet & Handheld - showing hints / tips / tool while loading */
const LoadingDataScreen: FC<Props> = ({
  yOffset,
  animateOnMount,
  progress,
  progressText,
  onClose,
}) => {
  const { i18n } = useI18n();

  const isHandyBreakpoint = window.innerWidth <= 600;

  const [currentFactIndex, setCurrentFactIndex] = useState<number>(
    Math.floor(Math.random() * DID_YOU_KNOWS.length),
  );
  const [isVisible, setIsVisible] = useState<boolean>(true);

  const closeButton = useButton({
    onClick: onClose,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);

      setTimeout(() => {
        /** Ensure we never get the same index */
        if (DID_YOU_KNOWS.length > 1) {
          let newIndex = currentFactIndex;
          while (newIndex === currentFactIndex) {
            newIndex = Math.floor(Math.random() * DID_YOU_KNOWS.length);
          }
          setCurrentFactIndex(newIndex);
        } else {
          setCurrentFactIndex(0);
        }
        setIsVisible(true);
      }, 250);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentFactIndex]);

  return (
    <Modal isShowing={true}>
      <Wrapper key="wrapper" yOffset={yOffset} animateOnMount={animateOnMount}>
        <ContentWrapper>
          <TopRow>
            <CloseButton display="flex" alignItems="center" gap="small" {...closeButton}>
              <Icon
                src={CloseIcon}
                width={isHandyBreakpoint ? '24px' : '32px'}
                height={isHandyBreakpoint ? '24px' : '32px'}
              />{' '}
              {i18n('General.Cancel')}
            </CloseButton>
          </TopRow>

          <CenterContent>
            <LogoContainer>
              <Logo src={bLogo} alt="Baemingo Logo" />
            </LogoContainer>

            <DidYouKnowSection>
              <Heading
                level={isHandyBreakpoint ? 3 : 2}
                color="var(--colorPOSClassic3)"
                bottom={8}>
                {i18n('DidYouKnow.Title')}
              </Heading>
              <FactTextWrapper style={{ opacity: isVisible ? 1 : 0 }}>
                <Paragraph level={isHandyBreakpoint ? 5 : 3} color="var(--colorBlueGray100)">
                  {i18n(DID_YOU_KNOWS[currentFactIndex])}
                </Paragraph>
              </FactTextWrapper>
            </DidYouKnowSection>
          </CenterContent>

          <ProgressBar progress={progress} />
          <ProgressText>{progressText}</ProgressText>
        </ContentWrapper>
      </Wrapper>
    </Modal>
  );
};

export default LoadingDataScreen;
